import { isEmpty, isFinite, isFunction, isNumber, keys, omitBy, pick } from 'lodash-es'
import isEqual from 'lodash-es/isEqual'
import noop from 'lodash-es/noop'
import result from 'lodash-es/result'
import dynamic from 'next/dynamic'
import {
  ComponentType,
  ForwardedRef,
  forwardRef,
  memo,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import hash from 'stable-hash'
import {
  APILoadingStatus,
  APIProvider,
  APIProviderProps,
  useApiLoadingStatus,
} from '@vis.gl/react-google-maps'

import { addAnimation, Easing, Tween } from '@/utils/animation'
import { useOptimizedLatLng } from '@/feature/map/hooks/useOptimizedLatLng'
import { MapContext } from '@/feature/map'

import { ForwardedRefProp, forwardRefWrapper } from '@/lib/utils/forwardRefWrapper'

import styles from './map.module.css'

export type GoogleLoadProps = Omit<APIProviderProps, 'apiKey'> & {
  apiKey?: string

  wrapperComponent?: ComponentType<PropsWithChildren>
}

const LoaderWrapper = ({ children }: PropsWithChildren) => {
  const status = useApiLoadingStatus()
  
  if (status === APILoadingStatus.FAILED) {
    return <LoadError />
  }
  if (status === APILoadingStatus.LOADING) {
    return <Loader />
  }
  if (status === APILoadingStatus.LOADED) {
    return children
  }
}

const Loader = () => <div>Loading...</div>
const LoadError = () => <div>Error loading Google Maps, please try to refresh</div>
export const GoogleLoader = ({
  apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string,
  libraries = ['drawing', 'geometry', 'places', 'visualization', 'geocoding', 'marker', 'maps', 'routes'],
  wrapperComponent = LoaderWrapper,
  children,
  ...rest
}: GoogleLoadProps) => {
  const Loader = wrapperComponent
  return (
    <APIProvider apiKey={apiKey} libraries={libraries} version="beta" {...rest}>
      <Loader>{children}</Loader>
    </APIProvider>
  )
}

export type MapRenderProps = {
  children?: ReactNode

  loaderProps?: GoogleLoadProps

  mapOptions?: google.maps.MapOptions | (() => google.maps.MapOptions)
  center?: google.maps.MapOptions['center']
  zoom?: google.maps.MapOptions['zoom']

  mapCallback?: (map: MapContext) => void

  onClick?: (event: google.maps.IconMouseEvent, context: MapContext) => void
  onDrag?: (context: MapContext) => void
  onDragStart?: (context: MapContext) => void
  onDragEnd?: (context: MapContext) => void
  onIdle?: (context: MapContext) => void
  onContextMenu?: (event: google.maps.MapMouseEvent, context: MapContext) => void
  onDoubleClick?: (event: google.maps.MapMouseEvent, context: MapContext) => void
  onZoomChanged?: (context: MapContext) => void
  onBoundsChanged?: (context: MapContext) => void
  onCenterChanged?: (context: MapContext) => void
  onHeadingChanged?: (context: MapContext) => void
  onTiltChanged?: (context: MapContext) => void
  onProjectionChanged?: (event: google.maps.MapMouseEvent, context: MapContext) => void
  onLongPress?: (event: google.maps.MapMouseEvent, context: MapContext) => void
}
const MapContainer = forwardRef<HTMLDivElement>(function MapContainer(_, ref) {
  return <div ref={ref} className="map" />
})

const MapContainerMemo = memo(MapContainer)

export const MapRenderer = (
  {
    children,
    mapOptions,
    center,
    zoom,
    mapCallback = noop,
    onClick,
    onDrag,
    onDragStart,
    onDragEnd,
    onZoomChanged,
    onContextMenu,
    onIdle,
    onDoubleClick,
    onBoundsChanged,
    onCenterChanged,
    onHeadingChanged,
    onTiltChanged,
    onProjectionChanged,
    onLongPress,
  }: MapRenderProps = {},
  ref: ForwardedRef<MapContext>,
) => {
  const mapElementRef = useRef(null)
  const [googleMapRef, setGoogleMapRef] = useState<MapContext | null>(null)

  const optionsChanged = hash(isFunction(mapOptions) ? mapOptions() : mapOptions)
  const locationHash = hash(center)

  const OptimizedLatLng = useOptimizedLatLng()
  const optimisedPosition = useMemo(() => new OptimizedLatLng(center ?? { lat: 0, lng: 0 }), [])
  const tweenOptimisation = useRef<{
    tilt?: number
    zoom?: number
    heading?: number
    center?: typeof optimisedPosition
  }>({
    center: optimisedPosition,
    tilt: 0,
    zoom: 0,
  })

  const animation = useRef<Tween<Parameters<google.maps.Map['moveCamera']>[0]> | null>(null)

  useEffect(() => {
    if (mapElementRef.current) {
      const map = new window.google.maps.Map(mapElementRef.current, {
        center,
        zoom,
        mapId: process.env.NEXT_PUBLIC_MAP_ID,
        ...result({ mapOptions }, 'mapOptions', {}),
      })

      if (map.getCenter() == null) {
        console.warn(
          "Map position isn't set, things may behave strangely (e.g. clusterer will error when adding markers)",
        )
      }
      if (map.getZoom() == null) {
        console.warn("Map zoom isn't set, things may behave strangely (e.g. clusterer will error)")
      }

      const mapRef: MapContext = {
        map,
        panTo(target, duration = 1_000 /** 1s is a mirror of Tween.js {@link Tween.to}*/) {
          if (target && map.getCenter() != map.getZoom()) {
            const goal = pick(
              omitBy(target, (v) => v == null || (isNumber(v) && !isFinite(v))),
              ['center', 'zoom', 'tilt', 'heading'],
            )

            if ('position' in target && target.position != null) {
              if (target.position instanceof window.google.maps.LatLng) {
                goal.center = target.position.toJSON()
              } else {
                goal.center = target.position
              }
            }

            const start = pick(
              {
                center: map.getCenter()?.toJSON(),
                heading: map.getHeading(),
                tilt: map.getTilt(),
                zoom: map.getZoom(),
              },
              keys(goal),
            )

            if (isEmpty(goal) || isEqual(start, goal)) return

            const oldAnimation = animation.current

            const newAnimation = new Tween(start)
              .to(goal, duration)
              .onStart(() => {
                oldAnimation?.stop()
              })
              .easing(Easing.Quadratic.In)
              .onUpdate((v) => {
                if (v.center) {
                  tweenOptimisation.current.center = optimisedPosition
                  tweenOptimisation.current.center.latitude = v.center.lat
                  tweenOptimisation.current.center.longitude = v.center.lng
                } else {
                  tweenOptimisation.current.center = undefined
                }
                tweenOptimisation.current.tilt = v.tilt!
                tweenOptimisation.current.zoom = v.zoom!

                map.moveCamera(tweenOptimisation.current)
              })
              .onComplete(() => {
                animation.current = null
              })
              .start()
            animation.current = newAnimation
            addAnimation(newAnimation)
            return animation.current
          }
        },
      }

      setGoogleMapRef(mapRef)

      if (ref) {
        if (typeof ref === 'function') {
          ref(mapRef)
        } else {
          ref.current = mapRef
        }
      }

      const fullscreenChange = (event: Event) => {
        const target = event.target as HTMLElement
        if (mapRef.map.getDiv().contains(target)) {
          if (document.fullscreenElement) {
            mapRef.map.getDiv().classList.add(styles.fullscreen)
          } else {
            mapRef.map.getDiv().classList.remove(styles.fullscreen)
          }
        }
      }

      document.addEventListener('fullscreenchange', fullscreenChange)
      return () => {
        document.removeEventListener('fullscreenchange', fullscreenChange)
        setGoogleMapRef(null)
        if (ref) {
          if (typeof ref === 'function') {
            ref(null)
          } else {
            ref.current = null
          }
        }
      }
    }
  }, [mapElementRef.current, optionsChanged])

  useEffect(() => {
    googleMapRef?.panTo({
      position: center,
    })
  }, [locationHash, googleMapRef])

  useEffect(() => {
    googleMapRef?.panTo({
      zoom,
    })
  }, [zoom, googleMapRef])

  useEffect(() => {
    if (googleMapRef) {
      mapCallback(googleMapRef)
    }
  }, [mapCallback, googleMapRef])

  let longpress = false

  useEffect(() => {
    if (googleMapRef) {
      const listeners: google.maps.MapsEventListener[] = []

      if (onClick || onLongPress) {
        listeners.push(
          googleMapRef.map.addListener('click', (...args: [google.maps.IconMouseEvent]) => {
            if (longpress) {
              onLongPress?.(...args, googleMapRef)
            } else {
              onClick?.(...args, googleMapRef)
            }
          }),
        )
      }

      if (onDrag) {
        listeners.push(googleMapRef.map.addListener('drag', () => onDrag(googleMapRef)))
      }

      if (onDragStart) {
        listeners.push(
          googleMapRef.map.addListener('dragstart', () => {
            longpress = true
            onDragStart(googleMapRef)
          }),
        )
      }

      if (onDragEnd) {
        listeners.push(googleMapRef.map.addListener('dragend', () => onDragEnd(googleMapRef)))
      }

      if (onZoomChanged) {
        listeners.push(
          googleMapRef.map.addListener('zoom_changed', () => onZoomChanged(googleMapRef)),
        )
      }

      if (onContextMenu) {
        listeners.push(
          googleMapRef.map.addListener('contextmenu', (...args: [google.maps.MapMouseEvent]) =>
            onContextMenu(...args, googleMapRef),
          ),
        )
      }

      if (onIdle) {
        listeners.push(googleMapRef.map.addListener('idle', () => onIdle(googleMapRef)))
      }

      if (onDoubleClick) {
        listeners.push(
          googleMapRef.map.addListener('dblclick', (...args: [google.maps.MapMouseEvent]) =>
            onDoubleClick(...args, googleMapRef),
          ),
        )
      }

      if (onBoundsChanged) {
        listeners.push(
          googleMapRef.map.addListener('bounds_changed', () => onBoundsChanged(googleMapRef)),
        )
      }

      if (onCenterChanged) {
        listeners.push(
          googleMapRef.map.addListener('center_changed', () => onCenterChanged(googleMapRef)),
        )
      }

      if (onHeadingChanged) {
        listeners.push(
          googleMapRef.map.addListener('heading_changed', () => onHeadingChanged(googleMapRef)),
        )
      }

      if (onTiltChanged) {
        listeners.push(
          googleMapRef.map.addListener('tilt_changed', () => onTiltChanged(googleMapRef)),
        )
      }

      if (onProjectionChanged) {
        listeners.push(
          googleMapRef.map.addListener(
            'projection_changed',
            (...args: [google.maps.MapMouseEvent]) => onProjectionChanged(...args, googleMapRef),
          ),
        )
      }

      // Detect longpress
      let start = new Date().getTime()
      let end = new Date().getTime()
      listeners.push(
        googleMapRef.map.addListener('mousedown', () => {
          start = new Date().getTime()
        }),
      )
      listeners.push(
        googleMapRef.map.addListener('mouseup', () => {
          end = new Date().getTime()
          longpress = end - start >= 500
        }),
      )

      return () => {
        listeners.forEach((listener) => google.maps.event.removeListener(listener))
      }
    }
  }, [
    googleMapRef,
    onBoundsChanged,
    onCenterChanged,
    onClick,
    onContextMenu,
    onDoubleClick,
    onDrag,
    onDragEnd,
    onDragStart,
    onHeadingChanged,
    onIdle,
    onProjectionChanged,
    onTiltChanged,
    onZoomChanged,
    onLongPress,
  ])

  return (
    <>
      <MapContext.Provider value={googleMapRef}>{children}</MapContext.Provider>
      <MapContainerMemo ref={mapElementRef} />
    </>
  )
}

export const MapRendererRef = forwardRef(MapRenderer)

export const MapHandlerLoader = ({
  loaderProps,
  forwardedRef,
  ...rest
}: MapRenderProps & ForwardedRefProp<MapContext>) => {
  return (
    <GoogleLoader {...loaderProps}>
      <MapRendererRef ref={forwardedRef} {...rest} />
    </GoogleLoader>
  )
}
// If this is on the server it causes hydration errors
export const MapHandlerDyn = dynamic(() => Promise.resolve(MapHandlerLoader), { ssr: false })
export const MapHandler = forwardRefWrapper(MapHandlerDyn)
